// Public company directory (recherche-entreprises.api.gouv.fr): no key, open to
// the browser. Legal forms and NAF sections are data saved on the company, not
// interface texts: they stay here, in French.
const DIRECTORY_URL = 'https://recherche-entreprises.api.gouv.fr/search';

const LEGAL_FORMS = {
  1000: 'Entrepreneur individuel',
  5410: 'SARL',
  5422: 'SARL',
  5426: 'SARL',
  5498: 'EURL',
  5499: 'SARL',
  5505: 'SA',
  5510: 'SA',
  5599: 'SA',
  5710: 'SAS',
  5720: 'SASU',
  5785: 'SELAS',
  5485: 'SELARL',
  5385: 'SELARL',
  6540: 'SCI',
  9220: 'Association',
  9210: 'Association',
};

const NAF_SECTIONS = {
  A: 'Agriculture, sylviculture et pêche',
  B: 'Industries extractives',
  C: 'Industrie manufacturière',
  D: 'Énergie',
  E: 'Eau et déchets',
  F: 'Construction',
  G: 'Commerce',
  H: 'Transports et entreposage',
  I: 'Hébergement et restauration',
  J: 'Information et communication',
  K: 'Finance et assurance',
  L: 'Immobilier',
  M: 'Activités spécialisées et techniques',
  N: 'Services administratifs et de soutien',
  O: 'Administration publique',
  P: 'Enseignement',
  Q: 'Santé humaine et action sociale',
  R: 'Arts, spectacles et loisirs',
  S: 'Autres services',
  T: 'Services domestiques',
  U: 'Organisations extraterritoriales',
};

// INSEE staff band → lower bound of the band.
const STAFF_BANDS = {
  '00': 0,
  '01': 1,
  '02': 3,
  '03': 6,
  11: 10,
  12: 20,
  21: 50,
  22: 100,
  31: 200,
  32: 250,
  41: 500,
  42: 1000,
  51: 2000,
  52: 5000,
  53: 10000,
};

const staffFromBand = (band) =>
  band in STAFF_BANDS ? STAFF_BANDS[band] : null;

// "FORT-DE-FRANCE" → "Fort-de-France": the small words stay lower case.
const SMALL_WORDS = new Set([
  'de',
  'du',
  'des',
  'la',
  'le',
  'les',
  'et',
  'sur',
  'en',
  'au',
  'aux',
  'd',
  'l',
]);
const toTitleCase = (text) =>
  String(text ?? '')
    .toLowerCase()
    .replace(/(^|[\s'-])(\p{L}+)/gu, (match, separator, word, offset) =>
      offset > 0 && SMALL_WORDS.has(word)
        ? separator + word
        : separator + word.charAt(0).toUpperCase() + word.slice(1),
    );

export class DirectoryError extends Error {
  constructor(code) {
    super(`Company directory: ${code}`);
    this.name = 'DirectoryError';
    this.code = code;
  }
}

const compact = (query) => String(query ?? '').replace(/\s/g, '');

export const isSiretQuery = (query) => /^\d{14}$/.test(compact(query));

export const formatSiret = (siret) =>
  siret
    ? `${siret.slice(0, 3)} ${siret.slice(3, 6)} ${siret.slice(6, 9)} ${siret.slice(9)}`
    : '';

const toDirectoryCompany = (result, siretQuery) => {
  const establishment =
    (siretQuery &&
      result.matching_etablissements?.find((e) => e.siret === siretQuery)) ||
    result.siege ||
    {};
  const street = [
    establishment.numero_voie,
    establishment.indice_repetition,
    establishment.type_voie,
    establishment.libelle_voie,
  ]
    .filter(Boolean)
    .join(' ');
  const leader = (result.dirigeants ?? []).find(
    (d) => d.type_dirigeant === 'personne physique',
  );
  const idcc =
    establishment.liste_idcc?.[0] ?? result.complements?.liste_idcc?.[0];
  const city = establishment.libelle_commune
    ? toTitleCase(establishment.libelle_commune)
    : '';
  // nom_complet appends the acronym or the sign in brackets: the trade name goes without it.
  const fullName = (
    result.nom_complet ||
    result.nom_raison_sociale ||
    ''
  ).replace(/\s*\(.*\)\s*$/, '');
  return {
    name: toTitleCase(establishment.liste_enseignes?.[0] || fullName),
    reasonSocial: (
      result.nom_raison_sociale ||
      result.nom_complet ||
      ''
    ).toUpperCase(),
    siret: establishment.siret ?? '',
    legalForm: LEGAL_FORMS[result.nature_juridique] ?? '',
    activity: NAF_SECTIONS[result.section_activite_principale] ?? '',
    codeApe: (result.activite_principale ?? '').toUpperCase(),
    staff: staffFromBand(
      establishment.tranche_effectif_salarie ?? result.tranche_effectif_salarie,
    ),
    address: establishment.adresse ? toTitleCase(establishment.adresse) : '',
    city,
    location: {
      street: toTitleCase(
        [street, establishment.complement_adresse].filter(Boolean).join(', '),
      ),
      postcode: establishment.code_postal ?? '',
      city,
      department: establishment.departement ?? '',
      lat: establishment.latitude ? Number(establishment.latitude) : undefined,
      lon: establishment.longitude
        ? Number(establishment.longitude)
        : undefined,
    },
    leader: leader
      ? {
          firstname: toTitleCase(String(leader.prenoms ?? '').split(' ')[0]),
          lastname: String(leader.nom ?? '').toUpperCase(),
        }
      : null,
    idcc: idcc ? String(idcc) : '',
  };
};

export const searchCompanyDirectory = async (query, { signal } = {}) => {
  const siretQuery = isSiretQuery(query) ? compact(query) : null;
  const q = encodeURIComponent(siretQuery || String(query ?? '').trim());
  let response;
  let data;
  try {
    response = await fetch(
      `${DIRECTORY_URL}?q=${q}&per_page=6&etat_administratif=A`,
      { signal },
    );
    if (response.ok) data = await response.json();
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new DirectoryError('unavailable');
  }
  if (!response.ok)
    throw new DirectoryError(
      response.status === 429 ? 'rateLimited' : 'unavailable',
    );
  return (data?.results ?? []).map((result) =>
    toDirectoryCompany(result, siretQuery),
  );
};
